<template>
	<div class="icon-upload">
		<div class="d-flex align-items-start gap-2">
			<!-- The STORED bytes, never the local file: the server sanitises an SVG
			     (drops scripts and styles) before it writes anything, so the object
			     url the browser would render is not what the map will show. -->
			<img
				v-if="iconSrc"
				:src="iconSrc"
				class="icon-upload-preview"
				alt="The stored icon"
			/>
			<div
				v-else
				class="icon-upload-preview icon-upload-preview--empty"
				title="No icon"
			>
				<i class="bi bi-image text-body-secondary"></i>
			</div>

			<div class="flex-grow-1">
				<input
					ref="fileInput"
					class="form-control form-control-sm"
					type="file"
					accept="image/png,image/svg+xml"
					:disabled="uploading"
					@change="upload"
				/>
				<p class="form-text mb-1">
					PNG or SVG, 256 KiB at most. The file is validated and normalised on
					the way in, so the preview above is the stored result rather than your
					file.
				</p>
				<ButtonLoading
					v-if="modelValue"
					text="Remove icon"
					icon="bi bi-x-lg"
					variant="outline-secondary"
					size="sm"
					:disabled="uploading"
					@click="clear"
				/>
			</div>
		</div>
		<div v-if="error" class="text-danger small mt-1" role="alert">
			{{ error }}
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import { symbolIconURL } from "@/lib/symbology";

/** POST /api/symbol/icon's answer: the stored identity, its media type and the
 * sanitised byte count. The uuid is the only part a caller keeps. */
interface SymbolIconResult {
	uuid: string;
	content_type: string;
	size_bytes: number;
}

interface Props {
	modelValue: string | null;
}
const props = defineProps<Props>();
const emit = defineEmits<{ "update:modelValue": [value: string | null] }>();

const fileInput = ref<HTMLInputElement | null>(null);
const uploading = ref(false);
const error = ref("");

const iconSrc = computed(() => symbolIconURL(props.modelValue));

async function upload() {
	const file = fileInput.value?.files?.[0];
	if (!file || uploading.value) return;
	error.value = "";
	uploading.value = true;
	// Exactly one part: the endpoint refuses a body with none or several, and the
	// part's own Content-Type is what the MIME gate reads. The explicit
	// multipart content type is what keeps apiClient's JSON default from
	// serialising the FormData instead of handing it to the browser, which then
	// sets the boundary itself.
	const body = new FormData();
	body.append("icon", file, file.name);
	try {
		const result = await apiClient.JSONPost<SymbolIconResult>(
			"/api/symbol/icon",
			body,
			{ headers: { "Content-Type": "multipart/form-data" } },
		);
		emit("update:modelValue", result.uuid);
	} catch (e: any) {
		error.value =
			e?.response?.data?.message || e?.message || "The icon upload failed.";
	} finally {
		uploading.value = false;
		if (fileInput.value) fileInput.value.value = "";
	}
}

function clear() {
	error.value = "";
	emit("update:modelValue", null);
}
</script>

<style scoped>
.icon-upload-preview {
	width: 40px;
	height: 40px;
	object-fit: contain;
	flex: 0 0 auto;
	background: #fff;
	border: 1px solid var(--bs-secondary-border-subtle);
	border-radius: var(--bs-border-radius);
}

.icon-upload-preview--empty {
	display: flex;
	align-items: center;
	justify-content: center;
}
</style>
